import { useState, useEffect, useMemo, useCallback } from 'react';
import { Flag, FolderKanban, CheckSquare } from 'lucide-react';
import { api } from '../api/client';
import { useT } from '../i18n';
import { FilterSelect, FilterToggle } from '../components/FilterSelect';
import { useRole } from '../context/RoleContext';
import type { EcheanceItem, EcheanceType, EcheancesResponse, Project, GridColumn, Task, Person, GeranceHistory } from '../types';
import { fmtShortDate } from '../utils/date';
import { getUrgency, daysOverdue, sortByDate, summarize } from '../utils/echeances';
import { GeranceCalendarView } from '../components/GeranceCalendarView';
import { GeranceResourcesView } from '../components/GeranceResourcesView';
import { GeranceResourceCompareView } from '../components/GeranceResourceCompareView';
import { GeranceInvolvementsView } from '../proposals/implications/GeranceInvolvementsView';

// PMO-wide échéances (deadlines) overview — Gérance. One consolidated,
// soonest-first view across all three deadline sources (project due dates,
// milestones, tasks), for the PMO to spot what's overdue or coming up without
// hunting through every project individually. Fixes #28.

const TYPE_ICON: Record<EcheanceType, typeof Flag> = {
  project: FolderKanban,
  milestone: Flag,
  task: CheckSquare,
};

function goToProject(item: EcheanceItem) {
  window.location.hash = `project?id=${item.projectId}`;
}

function openTask(item: EcheanceItem) {
  window.location.hash = `tasks?task=${item.taskId}`;
}

type GeranceView = 'calendar' | 'resources' | 'involvements' | 'list';
type ResourcesMode = 'person' | 'compare';

// Reads the active tab (and, for the resources tab, the selected person and
// single-person/compare mode) from the current hash so returning here via the
// browser Back button — after clicking into a project — lands back on the
// same tab/person instead of the default. Mirrors the read side of the
// id/from convention used elsewhere (e.g. ProjectDetailPage's
// `project?id=X&from=Y`). Landing on Gérance with no hash params defaults to
// the calendar tab — resources/list stay one click away.
function parseGeranceHash(): { view: GeranceView; personId: number | ''; resourcesMode: ResourcesMode } {
  const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const view = params.get('view');
  const person = params.get('person');
  const mode = params.get('mode');
  return {
    view: view === 'resources' || view === 'involvements' || view === 'list' ? view : 'calendar',
    personId: person ? Number(person) : '',
    resourcesMode: mode === 'person' ? 'person' : 'compare',
  };
}

function EcheanceRow({ item, t }: { item: EcheanceItem; t: (key: string, params?: Record<string, string | number>) => string }) {
  const urgency = getUrgency(item.date);
  const Icon = TYPE_ICON[item.type];
  const onOpen = item.type === 'task' ? () => openTask(item) : () => goToProject(item);

  return (
    <tr className="echeance-row" onClick={onOpen}>
      <td>
        <span className={`echeance-date${urgency !== 'normal' ? ` is-${urgency}` : ''}`}>
          <span className="echeance-dot" />
          {fmtShortDate(item.date)}
        </span>
        {urgency === 'overdue' && (
          <span className="echeance-late">{t('echeances.daysLate', { n: daysOverdue(item.date) })}</span>
        )}
      </td>
      <td>
        <span className="echeance-type">
          <Icon className="icon-sm icon-inline" />
          {t(`echeances.type.${item.type}`)}
        </span>
      </td>
      <td>
        <span className="font-medium text-ink-1">{item.title}</span>
        {item.type === 'task' && item.assignee && (
          <div className="text-xs text-ink-3">{item.assignee}</div>
        )}
      </td>
      <td>
        <span className="badge badge-accent">{item.projectCode || item.projectName}</span>
        {item.projectCode && <span className="text-xs text-ink-3" style={{ marginLeft: 'var(--s8)' }}>{item.projectName}</span>}
      </td>
    </tr>
  );
}

export function GerancePage() {
  const { t } = useT();
  // Everyone gets the same Resources view as admins (compare + per person,
  // over every project their account can see). The calendar stays scoped to
  // the projects a non-admin participates in (creator/sponsor/collaborator —
  // projectIds from useRole), and the échéances "list" tab is admin-only.
  const { isAdmin, projectIds, personName } = useRole();
  const [view, setView] = useState<GeranceView>(() => parseGeranceHash().view);
  const [resourcesPersonId, setResourcesPersonId] = useState<number | ''>(() => parseGeranceHash().personId);
  const [resourcesMode, setResourcesMode] = useState<ResourcesMode>(() => parseGeranceHash().resourcesMode);
  const [data, setData] = useState<EcheancesResponse>({ items: [], projects: [] });
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [pipelineColumns, setPipelineColumns] = useState<GridColumn[]>([]);
  const [kanbanColumns, setKanbanColumns] = useState<GridColumn[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [timelineHistory, setTimelineHistory] = useState<GeranceHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<EcheanceType | ''>('');
  const [projectFilter, setProjectFilter] = useState<number | ''>('');
  const [overdueOnly, setOverdueOnly] = useState(false);

  const load = useCallback(async () => {
    const [echeances, projects, pipeline, kanban, board, peopleList] = await Promise.all([
      api.getEcheances() as Promise<EcheancesResponse>,
      api.getProjects() as Promise<Project[]>,
      api.getGridColumns('pipeline') as Promise<GridColumn[]>,
      api.getGridColumns('kanban') as Promise<GridColumn[]>,
      api.getKanbanBoard() as Promise<{ projects: unknown[]; tasks: Task[] }>,
      api.getPeople() as Promise<Person[]>,
    ]);
    setData(echeances);
    setAllProjects(projects);
    setPipelineColumns(pipeline);
    setKanbanColumns(kanban);
    setTasks(board.tasks);
    setPeople(peopleList);
    setLoading(false);
    // The calendar's symbols and time slider: loaded after the page is up, the
    // calendar shows without them until they arrive.
    (api.getGeranceHistory() as Promise<GeranceHistory>).then(setTimelineHistory).catch(err =>
      console.error('Gérance history failed to load:', err));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Keep the current tab/person mirrored into the hash (replacing, not
  // pushing, so switching tabs doesn't spam the back-stack) — Back from a
  // project we navigated to from here then lands right back on this state.
  useEffect(() => {
    const params = new URLSearchParams();
    if (view !== 'calendar') params.set('view', view);
    if (view === 'resources' && resourcesMode === 'person') {
      params.set('mode', 'person');
      if (resourcesPersonId) params.set('person', String(resourcesPersonId));
    }
    const qs = params.toString();
    const newHash = '#gerance' + (qs ? `?${qs}` : '');
    if (window.location.hash !== newHash) {
      history.replaceState(null, '', newHash);
    }
  }, [view, resourcesPersonId, resourcesMode]);

  const stats = useMemo(() => summarize(data.items), [data.items]);

  const filtered = useMemo(() => {
    let result = data.items;
    if (typeFilter) result = result.filter(i => i.type === typeFilter);
    if (projectFilter) result = result.filter(i => i.projectId === projectFilter);
    if (overdueOnly) result = result.filter(i => getUrgency(i.date) === 'overdue');
    return sortByDate(result);
  }, [data.items, typeFilter, projectFilter, overdueOnly]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const i of data.items) counts[i.type] = (counts[i.type] || 0) + 1;
    return counts;
  }, [data.items]);

  const projectDeadlines = useMemo(() => {
    const map = new Map<number, string>();
    for (const item of data.items) if (item.type === 'project') map.set(item.projectId, item.date);
    return map;
  }, [data.items]);

  // Non-admins: restrict every view's data to their own projects. The board
  // endpoint already scopes tasks for role 'user', but not for 'direction', so
  // filter here too and key off the same projectIds the calendar/resources
  // views build their rows from.
  const visibleProjects = useMemo(
    () => isAdmin ? allProjects : allProjects.filter(p => projectIds.includes(p.id)),
    [isAdmin, allProjects, projectIds]
  );
  const visibleTasks = useMemo(
    () => isAdmin ? tasks : tasks.filter(tk => projectIds.includes(tk.project_id)),
    [isAdmin, tasks, projectIds]
  );

  // Non-admins open the Resources tab's per-person mode on their own row
  // (resolved by the account-linked name) until they pick someone else.
  const myPersonId = useMemo(() => {
    if (isAdmin || !personName) return '' as number | '';
    const me = people.find(p => p.name.trim().toLowerCase() === personName.trim().toLowerCase());
    return me ? me.id : ('' as number | '');
  }, [isAdmin, personName, people]);

  // The "list" (échéances table) tab is admin-only — bounce a non-admin who
  // lands on it via a stale hash back to the calendar.
  useEffect(() => {
    if (!isAdmin && view === 'list') setView('calendar');
  }, [isAdmin, view]);

  if (loading) {
    return <div className="page"><div className="spinner" /></div>;
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('gerance.title')}</h1>
          <p className="page-meta">{t('gerance.subtitle')}</p>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab${view === 'calendar' ? ' active' : ''}`} onClick={() => setView('calendar')}>
          {t('gerance.view.calendar')}
        </button>
        <button className={`tab${view === 'resources' ? ' active' : ''}`} onClick={() => setView('resources')}>
          {t('gerance.view.resources')}
        </button>
        {/* [Proposition: implications] */}
        <button className={`tab${view === 'involvements' ? ' active' : ''}`} onClick={() => setView('involvements')}>
          {t('gerance.view.involvements')}
        </button>
        {isAdmin && (
          <button className={`tab${view === 'list' ? ' active' : ''}`} onClick={() => setView('list')}>
            {t('gerance.view.list')}
          </button>
        )}
      </div>

      {view === 'calendar' ? (
        <GeranceCalendarView projects={visibleProjects} pipelineColumns={pipelineColumns} kanbanColumns={kanbanColumns} tasks={visibleTasks} projectDeadlines={projectDeadlines} history={timelineHistory} />
      ) : view === 'involvements' ? (
        <GeranceInvolvementsView />
      ) : view === 'resources' ? (
        // Everyone gets the same Resources view as admins (compare + per person),
        // over every project their account can see.
        <>
          <div className="filter-chips mb-16">
            <button className={`chip ${resourcesMode === 'compare' ? 'active' : ''}`} onClick={() => setResourcesMode('compare')}>
              {t('resources.modeCompare')}
            </button>
            <button className={`chip ${resourcesMode === 'person' ? 'active' : ''}`} onClick={() => setResourcesMode('person')}>
              {t('resources.modePerson')}
            </button>
          </div>
          {resourcesMode === 'compare' ? (
            <GeranceResourceCompareView
              projects={allProjects}
              pipelineColumns={pipelineColumns}
              people={people}
              projectDeadlines={projectDeadlines}
            />
          ) : (
            <GeranceResourcesView
              projects={allProjects}
              pipelineColumns={pipelineColumns}
              people={people}
              projectDeadlines={projectDeadlines}
              // A non-admin starts on their own row until they pick someone else.
              personId={resourcesPersonId || myPersonId}
              onPersonChange={setResourcesPersonId}
            />
          )}
        </>
      ) : !isAdmin ? null : (
      <>
      {/* Summary strip */}
      <div className="stats-grid mb-24">
        <button
          type="button"
          className={`stat-card stat-card-toggle${overdueOnly ? ' is-active' : ''}`}
          onClick={() => setOverdueOnly(v => !v)}
          aria-pressed={overdueOnly}
          title={t('echeances.stat.overdueHint')}
        >
          <div className="stat-value" style={{ color: stats.overdue > 0 ? 'var(--err)' : undefined }}>{stats.overdue}</div>
          <div className="stat-label">{t('echeances.stat.overdue')}</div>
        </button>
        <div className="stat-card">
          <div className="stat-value">{stats.dueThisWeek}</div>
          <div className="stat-label">{t('echeances.stat.dueWeek')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.dueThisMonth}</div>
          <div className="stat-label">{t('echeances.stat.dueMonth')}</div>
        </div>
      </div>

      {/* Toolbar: type chips, project select, overdue-only toggle */}
      <div className="toolbar">
        <div className="filter-chips">
          <button className={`chip ${typeFilter === '' ? 'active' : ''}`} onClick={() => setTypeFilter('')}>
            {t('echeances.filterAllTypes')} ({data.items.length})
          </button>
          {(['project', 'milestone', 'task'] as EcheanceType[]).map(ty => (
            <button
              key={ty}
              className={`chip ${typeFilter === ty ? 'active' : ''}`}
              onClick={() => setTypeFilter(typeFilter === ty ? '' : ty)}
            >
              {t(`echeances.type.${ty}`)} ({typeCounts[ty] || 0})
            </button>
          ))}
        </div>

        <FilterSelect
          label={t('filters.project')}
          multiple={false}
          width={280}
          selected={projectFilter === '' ? [] : [projectFilter]}
          options={data.projects.map(p => ({ value: p.id, label: p.project_code ? `${p.project_code.toUpperCase()} — ${p.name}` : p.name }))}
          onChange={vals => setProjectFilter(vals[0] ?? '')}
        />
        <FilterToggle label={t('echeances.overdueOnly')} checked={overdueOnly} onChange={setOverdueOnly} />
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">{t('echeances.emptyTitle')}</div>
          <div className="empty-state-desc">
            {typeFilter || projectFilter || overdueOnly ? t('echeances.emptyDescFilter') : t('echeances.emptyDescDefault')}
          </div>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 120 }}>{t('echeances.col.date')}</th>
              <th style={{ width: 130 }}>{t('echeances.col.type')}</th>
              <th>{t('echeances.col.item')}</th>
              <th>{t('echeances.col.project')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(item => <EcheanceRow key={item.id} item={item} t={t} />)}
          </tbody>
        </table>
      )}
      </>
      )}
    </>
  );
}
